import { AHText, ListItem } from "arkh";
import React, { useCallback, useMemo } from "react";
import { ListRenderItem } from "react-native";
import { graphql, useFragment } from "react-relay";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { useModalInsets } from "../../../../../components/mobile/useModalInsets";
import { RoutineSectionListItem } from "../WidgetTaskSingle/RoutineSectionListItem";
import { SelectList } from "arkh/src/cross-platform/SelectList/SelectList";
import { SelectListItem } from "arkh/src/cross-platform/SelectList/SelectListItem";
import { WAKPISettings_widgetTaskSingle$key } from "./__generated__/WAKPISettings_widgetTaskSingle.graphql";

export function WAKPISettings(props: {
  sectionIds: string[];
  setSectionIds(_: React.SetStateAction<string[]>): void;
  widget: WAKPISettings_widgetTaskSingle$key;
}) {
  const task = useFragment(
    graphql`
      fragment WAKPISettings_widgetTaskSingle on WidgetTaskSingle {
        id
        activeSectionVersions {
          id
          section {
            id
            assignedTeam {
              id
              name
            }
          }
          ...RoutineSectionListItem_RoutineSectionVersion
        }
      }
    `,
    props.widget
  );

  const allPossibleSectionIds = task.activeSectionVersions.map(
    (v) => v.section.id
  );
  const setSectionIds = props.setSectionIds;
  const onItemPress = useCallback(
    (itemId: string, selected: boolean) => {
      if (selected) {
        setSectionIds((ids) => ids.filter((id) => id !== itemId));
      } else {
        setSectionIds((s) => {
          const newIds = [...s, itemId];
          if (allPossibleSectionIds.every((id) => newIds.includes(id))) {
            return [];
          }
          return newIds;
        });
      }
    },
    [allPossibleSectionIds, setSectionIds]
  );

  const renderItem: ListRenderItem<
    (typeof task.activeSectionVersions)[number]
  > = useCallback(
    ({ item, index }) => {
      const selected = props.sectionIds.includes(item.section.id);
      return (
        <RoutineSectionListItem
          isFirst={index === 0}
          isLast={index === task.activeSectionVersions.length - 1}
          onPress={() => {
            onItemPress(item.section.id, selected);
          }}
          section={item}
          right={<ListItem.SelectIndicator selected={selected} />}
        />
      );
    },
    [task.activeSectionVersions.length, onItemPress, props.sectionIds]
  );

  const onPressAllMeasureAreas = useCallback(() => {
    props.setSectionIds([]);
  }, [props]);

  const intl_tokens = useIntlTokens();

  const listData = useMemo(
    () =>
      task.activeSectionVersions
        .slice()
        .sort((a, b) =>
          (a.section?.assignedTeam?.name ?? "") >
          (b.section?.assignedTeam?.name ?? "")
            ? 1
            : -1
        ),
    [task.activeSectionVersions]
  );

  const insets = useModalInsets();

  return (
    <SelectList
      contentBox={{
        spaceTop: "medium",
      }}
      insets={insets}
      ListHeaderComponent={
        <SelectListItem
          isFirst
          isLast
          onPress={onPressAllMeasureAreas}
          selected={props.sectionIds.length === 0}
        >
          <AHText type="title-small" style={{ flex: 1 }}>
            {intl_tokens.AllMesurementAreas}
          </AHText>
        </SelectListItem>
      }
      data={listData}
      renderItem={renderItem}
      keyExtractor={(item) => item.id}
    />
  );
}
